import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
	Autocomplete,
	TextField,
	Box,
	Chip,
	Stack,
	CircularProgress,
	InputAdornment,
	IconButton,
	useTheme,
	Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { clientSearch } from '@/utils/typesense/clients/clientSearch';
import { dealSearch } from '@/utils/typesense/clients/dealSearch';
import { taskSearch } from '@/utils/typesense/clients/taskSearch';
import { MIN_QUERY_LENGTH, SEARCH_DEBOUNCE_MS } from '@/utils/constants';
import { getObjectChipProps } from '@/utils/objectChip';
import { relationshipCompanySearch } from '@/utils/typesense/clients/relationshipCompanySearch';
import { isModifiedClick } from '@/utils/navigation';
import {
	getSearchRecordNavigateOptions,
	getSearchRecordPath,
} from '@/components/tables/InstantSearchDataGrid/data';

const RELEVANCE_SORT = 'sort/_text_match:desc';

const hitsFromSearch = (settled, indexName) => {
	if (settled.status === 'rejected') {
		console.error(`[GlobalSearch] ${indexName} search failed`, settled.reason);
		return [];
	}
	return settled.value?.results?.[0]?.hits ?? [];
};

// Lower is closer: exact label, then prefix, then substring, then every query word.
const getMatchRank = (label, query) => {
	const normalisedLabel = (label ?? '').trim().toLowerCase();
	const normalisedQuery = query.trim().toLowerCase();
	if (normalisedLabel === normalisedQuery) return 0;
	if (normalisedLabel.startsWith(normalisedQuery)) return 1;
	if (normalisedLabel.includes(normalisedQuery)) return 2;
	if (normalisedQuery.split(/\s+/).every((word) => normalisedLabel.includes(word))) return 3;
	return 4;
};

export const GlobalSearch = () => {
	const navigate = useNavigate();
	const theme = useTheme();
	const [query, setQuery] = useState('');
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(false);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails, isLoading: userLoading } = useGetUserDetailsQuery(
		{ email },
		{ skip: !email },
	);

	const brokerGroupIds = userDetails?.brokerGroupArray ?? [];
	const canViewGroup = userDetails?.canViewGroup;
	const userId = userDetails?.userId ?? '__none__';

	const accessFilter = useMemo(() => {
		if (canViewGroup && brokerGroupIds.length > 0) {
			return `brokerGroupId: [${brokerGroupIds.join(',')}]`;
		}
		return `ownerDocId:=${userId}`;
	}, [canViewGroup, brokerGroupIds, userId]);

	const search = useCallback(
		async (searchQuery) => {
			if (!searchQuery || searchQuery.length < MIN_QUERY_LENGTH || !accessFilter) {
				setOptions([]);
				return;
			}

			setLoading(true);

			try {
				const [clientRes, dealRes, taskRes, companyRes] = await Promise.allSettled([
					clientSearch.search([
						{
							indexName: `clients/${RELEVANCE_SORT}`,
							params: {
								query: searchQuery,
								hitsPerPage: 5,
								filters: accessFilter,
							},
						},
					]),
					dealSearch.search([
						{
							indexName: `deals/${RELEVANCE_SORT}`,
							params: {
								query: searchQuery,
								hitsPerPage: 8,
								filters: accessFilter,
							},
						},
					]),
					taskSearch.search([
						{
							indexName: `tasks/${RELEVANCE_SORT}`,
							params: {
								query: searchQuery,
								hitsPerPage: 5,
								filters: accessFilter,
							},
						},
					]),
					relationshipCompanySearch.search([
						{
							indexName: `relationshipCompanies/${RELEVANCE_SORT}`,
							params: {
								query: searchQuery,
								hitsPerPage: 5,
								filters: accessFilter,
							},
						},
					]),
				]);

				const clientHits = hitsFromSearch(clientRes, 'clients').map((hit) => ({
					...hit,
					indexName: 'clients',
					label: hit.name,
				}));

				const dealHits = hitsFromSearch(dealRes, 'deals').map((hit) => ({
					...hit,
					indexName: 'deals',
					label: hit.dealName,
				}));

				const taskHits = hitsFromSearch(taskRes, 'tasks').map((hit) => ({
					...hit,
					indexName: 'tasks',
					label: hit.subject,
				}));
				const companyHits = hitsFromSearch(companyRes, 'relationshipCompanies').map(
					(hit) => ({
						...hit,
						indexName: 'relationshipCompanies',
						label: hit.name,
					}),
				);

				// Stable sort: ties keep type order and each collection's relevance order.
				setOptions(
					[...clientHits, ...dealHits, ...taskHits, ...companyHits].sort(
						(a, b) =>
							getMatchRank(a.label, searchQuery) - getMatchRank(b.label, searchQuery),
					),
				);
			} catch (error) {
				console.error('[GlobalSearch] search failed', error);
				setOptions([]);
			} finally {
				setLoading(false);
			}
		},
		[accessFilter],
	);

	useEffect(() => {
		const timeout = setTimeout(() => {
			search(query);
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(timeout);
	}, [query, search]);

	const handleSelect = (_, option) => {
		if (!option) {
			return;
		}

		const path = getSearchRecordPath(option);
		if (path) {
			navigate(path, getSearchRecordNavigateOptions(option));
		}

		setQuery('');
		setOptions([]);
		setOpen(false);
	};

	const handleClear = () => {
		setQuery('');
		setOptions([]);
	};

	const getGlobalSearchChipProps = (option, theme) => {
		switch (option.indexName) {
			case 'deals':
				return getObjectChipProps('deal', {
					pipelineId: option.pipeline,
					theme,
				});
			case 'clients':
				return getObjectChipProps('client', {
					isProspect: option.isProspect,
					theme,
				});
			case 'tasks':
				return getObjectChipProps('task', { theme });
			case 'relationshipCompanies':
				return getObjectChipProps('company', { theme });
			default:
				return null;
		}
	};
	return (
		<Autocomplete
			id='global-search'
			sx={{ width: '100%' }}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			options={query.length >= MIN_QUERY_LENGTH ? options : []}
			loading={loading}
			loadingText='Searching...'
			disabled={userLoading}
			disableClearable
			forcePopupIcon={false}
			filterOptions={(x) => x}
			noOptionsText={query.length >= MIN_QUERY_LENGTH ? 'No results found' : 'Type to search'}
			inputValue={query}
			onInputChange={(_, value, reason) => {
				if (reason !== 'reset') {
					setQuery(value);
				}
			}}
			slotProps={{ listbox: { sx: { py: 0.5 } } }}
			getOptionLabel={(option) => option.label ?? ''}
			getOptionKey={(option) => `global-search-${option.indexName}-${option.id}`}
			isOptionEqualToValue={(option, value) =>
				option.indexName === value.indexName && option.id === value.id
			}
			onChange={handleSelect}
			renderOption={(props, option) => {
				const { key, ...optionProps } = props;
				const href = getSearchRecordPath(option);
				const objectChip = getGlobalSearchChipProps(option, theme);

				return (
					<Box
						component='li'
						{...optionProps}
						key={key}
						{...(href && {
							component: RouterLink,
							to: href,
							// Modified clicks open the link in a new tab; plain clicks select the option.
							onClick: (event) => {
								if (isModifiedClick(event)) {
									setQuery('');
									setOptions([]);
									setOpen(false);
									return;
								}
								event.preventDefault();
								optionProps.onClick(event);
							},
							sx: { textDecoration: 'none', color: 'inherit' },
						})}
					>
						<Stack direction='row' alignItems='center' spacing={1}>
							{objectChip && (
								<Chip
									size='small'
									variant='outlined'
									color='primary'
									label={objectChip.label}
									sx={{
										borderColor: objectChip.borderColor,
										color: objectChip.color,
									}}
								/>
							)}
							<Typography variant='inherit'>{option.label}</Typography>
						</Stack>
					</Box>
				);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder='Search clients, deals, tasks, relationships...'
					size='small'
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							search(query);
						}
					}}
					slotProps={{
						htmlInput: {
							...params.inputProps,
							'data-testid': 'global-search',
						},
						input: {
							...params.InputProps,
							startAdornment: (
								<InputAdornment position='start'>
									<IconButton
										disabled={userLoading || loading}
										sx={{ padding: '0' }}
										onClick={() => search(query)}
									>
										<SearchIcon />
									</IconButton>
								</InputAdornment>
							),
							endAdornment: (
								<>
									{loading ? (
										<CircularProgress color='inherit' size={20} />
									) : null}
									{params.InputProps.endAdornment}
									{query.length > 0 && (
										<IconButton
											size='small'
											disabled={userLoading || loading}
											sx={{ padding: '0' }}
											onClick={handleClear}
										>
											<ClearIcon fontSize='small' />
										</IconButton>
									)}
								</>
							),
						},
					}}
				/>
			)}
		/>
	);
};
